import React from 'react';
import { Grid, Paper, Typography, Skeleton, Box } from '@mui/material';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	ArcElement,
	TimeScale,
	Tooltip,
	Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { format } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, TimeScale, Tooltip, Legend);

const palette = ['#1976d2', '#9c27b0', '#2e7d32', '#ff9800', '#ef5350', '#2196f3', '#ab47bc', '#26a69a'];

export function AuditLogCharts({ logs = [], loading }) {
	const actions = React.useMemo(() => {
		const counts = {};
		logs.forEach(item => {
			const key = item?.eventAction || 'unknown';
			counts[key] = (counts[key] || 0) + 1;
		});
		return counts;
	}, [logs]);

	const actors = React.useMemo(() => {
		const counts = { user: 0, system: 0, unknown: 0 };
		logs.forEach(item => {
			const key = item?.actorType || 'unknown';
			counts[key] = (counts[key] || 0) + 1;
		});
		return counts;
	}, [logs]);

	const timeline = React.useMemo(() => {
		const grouped = {};
		logs.forEach(item => {
			if (!item?.timestamp) return;
			const d = new Date(item.timestamp);
			if (Number.isNaN(d.getTime())) return;
			const key = format(d, 'yyyy-MM-dd');
			grouped[key] = (grouped[key] || 0) + 1;
		});
		return Object.entries(grouped)
			.map(([date, count]) => ({ date, count }))
			.sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [logs]);

	const barData = {
		labels: Object.keys(actions),
		datasets: [
			{
				label: 'Events',
				data: Object.values(actions),
				backgroundColor: palette
			}
		]
	};

	const donutData = {
		labels: Object.keys(actors),
		datasets: [
			{
				label: 'Actor type',
				data: Object.values(actors),
				backgroundColor: ['#1b5e20', '#1565c0', '#f57c00']
			}
		]
	};

	const lineData = {
		datasets: [
			{
				label: 'Events over time',
				data: timeline.map(point => ({ x: point.date, y: point.count })),
				borderColor: '#1976d2',
				backgroundColor: 'rgba(25, 118, 210, 0.15)',
				tension: 0.3,
				fill: true,
				pointRadius: 3,
				pointHoverRadius: 5
			}
		]
	};

	const chartHeights = {
		bar: 220,
		donut: 200,
		line: 200
	};

	const BasePaper = props => (
		<Paper
			variant="outlined"
			sx={{
				p: 2,
				borderRadius: 3,
				height: 300,
				maxHeight: 300,
				bgcolor: 'background.paper',
				boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
				display: 'flex',
				flexDirection: 'column'
			}}
			{...props}
		/>
	);

	const isEmpty = !loading && logs.length === 0;

	if (loading) {
		return (
			<Grid container spacing={2}>
				{[1, 2, 3].map(key => (
					<Grid item xs={12} md={4} key={key}>
						<BasePaper>
							<Skeleton variant="text" width="60%" />
							<Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2, mt: 2 }} />
						</BasePaper>
					</Grid>
				))}
			</Grid>
		);
	}

	if (isEmpty) {
		<Grid container spacing={2}>
			{[1, 2, 3].map(key => (
				<Grid item xs={12} md={4} key={key}>
					<BasePaper>
						<Typography variant="subtitle1" fontWeight={700}>
							No data yet
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Charts will appear when logs are available.
						</Typography>
					</BasePaper>
				</Grid>
			))}
		</Grid>
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} xl={6}>
				<Paper
					variant="outlined"
					sx={{ p: 2, borderRadius: 3, height: 300, maxHeight: 300, bgcolor: 'background.paper', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}
				>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
						Events by Action
					</Typography>
					<Box sx={{ flex: 1, minHeight: chartHeights.bar }}>
						<Bar
							data={barData}
							options={{
								maintainAspectRatio: false,
								plugins: { legend: { display: false } },
								scales: { y: { ticks: { precision: 0 }, beginAtZero: true } }
							}}
							style={{ height: '100%' }}
						/>
					</Box>
				</Paper>
			</Grid>
			<Grid item xs={12} xl={3}>
				<Paper
					variant="outlined"
					sx={{ p: 2, borderRadius: 3, height: 300, maxHeight: 300, bgcolor: 'background.paper', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}
				>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
						Events by Actor
					</Typography>
					<Box sx={{ maxWidth: 320, mx: 'auto', flex: 1, minHeight: chartHeights.donut }}>
						<Doughnut
							data={donutData}
							options={{
								maintainAspectRatio: false,
								plugins: {
									legend: { position: 'bottom' },
									tooltip: {
										callbacks: {
											label: ctx => {
												const total = ctx.dataset.data.reduce((acc, val) => acc + val, 0);
												const value = ctx.parsed;
												const percent = total ? Math.round((value / total) * 100) : 0;
												return `${ctx.label}: ${value} (${percent}%)`;
											}
										}
									}
								}
							}}
							style={{ height: '100%' }}
						/>
					</Box>
				</Paper>
			</Grid>
			<Grid item xs={12} xl={3}>
				<Paper
					variant="outlined"
					sx={{ p: 2, borderRadius: 3, height: 300, maxHeight: 300, bgcolor: 'background.paper', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}
				>
					<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
						Timeline
					</Typography>
					<Box sx={{ flex: 1, minHeight: chartHeights.line }}>
						<Line
							data={lineData}
							options={{
								maintainAspectRatio: false,
								plugins: { legend: { display: false } },
								scales: {
									x: {
										type: 'time',
										time: { unit: 'day' },
										grid: { display: false }
									},
									y: { ticks: { precision: 0 }, beginAtZero: true }
								}
							}}
							style={{ height: '100%' }}
						/>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
}
